import EditableChartTitle from "./EditableChartTitle.vue";

// HCI-4741: shared print behaviour for the Analysis report charts.
//
// A customer printed two different reports and both pages said "Infection
// Rates". They could not tell the two pages apart.
//
// This mixin turns the chart title into a normal page element that the user
// can edit. The Highcharts title is removed. The editable heading then shows
// on screen and on every print, because the browser prints the page.
//
// There are two print paths in the app, and both must show the new title:
//   1. The print button in the app header. It calls window.print().
//   2. The "Print chart" item in the Highcharts menu. That item normally
//      hides the rest of the page and prints the chart alone (see
//      `beforePrint` in node_modules/highcharts/modules/exporting.src.js).
//      The mixin replaces it, so it prints the whole page as well.
//
// The image and PDF downloads still export the chart alone. The mixin puts
// the edited title into `exporting.chartOptions`, so those files keep a title.
//
// A chart component that uses this mixin must do two things:
//   1. put `<editable-chart-title v-if="printable" v-model="reportTitle" />`
//      above its `<highcharts>` element;
//   2. pass its final Highcharts options through `applyPrintableTitle()`
//      inside a computed property.
// A page turns the behaviour on with the `printable` prop.

export default {
  components: {
    "editable-chart-title": EditableChartTitle,
  },
  props: {
    // Set this to true on the Analysis report pages. All other pages keep the
    // standard Highcharts title and the standard chart-only print.
    printable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // What the user typed. Null means the user has not edited the title, so
      // the report keeps following the `title` prop.
      editedTitle: null,
    };
  },
  computed: {
    // The title shown above the chart. Use with v-model.
    reportTitle: {
      get() {
        return this.editedTitle === null ? this.title || "" : this.editedTitle;
      },
      set(value) {
        this.editedTitle = value;
      },
    },
  },
  methods: {
    // Remove the Highcharts title, keep the title in the exported files, and
    // make the Highcharts print menu item print the whole page.
    // Call this on the final options object, inside a computed property.
    applyPrintableTitle(config) {
      if (!this.printable) {
        return config;
      }
      const exporting = config.exporting || {};
      // A downloaded image or PDF holds the chart alone, so it needs the title
      // inside the chart. Read the title when the user clicks the menu item.
      // Do not read it here: this method runs inside a computed property, so
      // the title would become a dependency, and every key press would send
      // new options to `chart.update()`.
      const exportTitle = () => ({ title: { text: this.reportTitle } });
      return Object.assign({}, config, {
        // The editable page element shows the title instead.
        title: Object.assign({}, config.title, { text: null }),
        exporting: Object.assign({}, exporting, {
          // Highcharts calls each `onclick` with the chart as `this`.
          menuItemDefinitions: Object.assign({}, exporting.menuItemDefinitions, {
            printChart: {
              // `textKey` keeps the standard "Print chart" label.
              textKey: "printChart",
              // Print the page, not the chart alone. Highcharts closes the
              // menu before it calls this handler.
              onclick: () => window.print(),
            },
            downloadPNG: {
              textKey: "downloadPNG",
              onclick() {
                this.exportChart({}, exportTitle());
              },
            },
            downloadJPEG: {
              textKey: "downloadJPEG",
              onclick() {
                this.exportChart({ type: "image/jpeg" }, exportTitle());
              },
            },
            downloadPDF: {
              textKey: "downloadPDF",
              onclick() {
                this.exportChart({ type: "application/pdf" }, exportTitle());
              },
            },
            downloadSVG: {
              textKey: "downloadSVG",
              onclick() {
                this.exportChart({ type: "image/svg+xml" }, exportTitle());
              },
            },
          }),
        }),
      });
    },
  },
};
